/**
 * Backend cloud RÉEL — Firebase Authentication (email/mot de passe + email de réinitialisation)
 * + Cloud Firestore (sauvegarde de la progression). Le SDK Firebase est importé DYNAMIQUEMENT :
 * il n'alourdit pas le bundle initial ni le solo hors ligne — il n'est chargé qu'à l'usage.
 *
 * Config publique injectée au build via `VITE_FIREBASE_CONFIG` (JSON). La sécurité repose sur
 * Firebase Auth + les règles Firestore (chaque utilisateur n'accède qu'à son propre document),
 * pas sur le secret de ces clés — elles sont conçues pour vivre dans le client.
 */
import type { AuthResult, CloudBackend, CloudUser, SavePayload } from './types';
import type { FirebaseConfig } from './config';

/** Traduit un code d'erreur Firebase Auth en message FR. */
function mapError(code: string): string {
  const m: Record<string, string> = {
    'auth/email-already-in-use': 'Cet email a déjà un compte.',
    'auth/invalid-email': "L'email n'est pas valide.",
    'auth/weak-password': 'Mot de passe : 6 caractères minimum.',
    'auth/missing-password': 'Entre un mot de passe.',
    'auth/invalid-credential': 'Email ou mot de passe incorrect.',
    'auth/wrong-password': 'Email ou mot de passe incorrect.',
    'auth/user-not-found': 'Email ou mot de passe incorrect.',
    'auth/too-many-requests': 'Trop de tentatives — réessaie dans un instant.',
    'auth/network-request-failed': 'Réseau indisponible (hors ligne ?).',
  };
  return m[code] ?? 'Une erreur est survenue.';
}
function errCode(e: unknown): string {
  return typeof e === 'object' && e && 'code' in e ? String((e as { code: unknown }).code) : '';
}

export class FirebaseBackend implements CloudBackend {
  private ready: Promise<void>;
  private settled = false;
  private user: CloudUser | null = null;
  private cb: ((u: CloudUser | null) => void) | null = null;

  // Handles résolus dans init() (dynamiquement importés).
  private auth!: import('firebase/auth').Auth;
  private db!: import('firebase/firestore').Firestore;
  private fns!: {
    createUser: typeof import('firebase/auth').createUserWithEmailAndPassword;
    signIn: typeof import('firebase/auth').signInWithEmailAndPassword;
    signOut: typeof import('firebase/auth').signOut;
    resetEmail: typeof import('firebase/auth').sendPasswordResetEmail;
    doc: typeof import('firebase/firestore').doc;
    getDoc: typeof import('firebase/firestore').getDoc;
    setDoc: typeof import('firebase/firestore').setDoc;
  };

  constructor(cfg: FirebaseConfig) {
    this.ready = this.init(cfg);
  }

  private async init(cfg: FirebaseConfig): Promise<void> {
    const { initializeApp } = await import('firebase/app');
    const {
      getAuth,
      onAuthStateChanged,
      browserLocalPersistence,
      setPersistence,
      createUserWithEmailAndPassword,
      signInWithEmailAndPassword,
      signOut,
      sendPasswordResetEmail,
    } = await import('firebase/auth');
    const { getFirestore, doc, getDoc, setDoc } = await import('firebase/firestore');

    const app = initializeApp(cfg);
    this.auth = getAuth(app);
    await setPersistence(this.auth, browserLocalPersistence).catch(() => {});
    this.db = getFirestore(app);
    this.fns = {
      createUser: createUserWithEmailAndPassword,
      signIn: signInWithEmailAndPassword,
      signOut,
      resetEmail: sendPasswordResetEmail,
      doc,
      getDoc,
      setDoc,
    };
    onAuthStateChanged(this.auth, (u) => {
      this.settled = true;
      this.user = u ? { uid: u.uid, email: u.email ?? '' } : null;
      this.cb?.(this.user);
    });
  }

  onAuth(cb: (u: CloudUser | null) => void): void {
    this.cb = cb;
    if (this.settled) cb(this.user); // observateur tardif : état courant tout de suite
  }
  currentUser(): CloudUser | null {
    return this.user;
  }

  async signUp(email: string, password: string): Promise<AuthResult> {
    await this.ready;
    try {
      await this.fns.createUser(this.auth, email.trim(), password);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: mapError(errCode(e)) };
    }
  }
  async signIn(email: string, password: string): Promise<AuthResult> {
    await this.ready;
    try {
      await this.fns.signIn(this.auth, email.trim(), password);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: mapError(errCode(e)) };
    }
  }
  async signOut(): Promise<void> {
    await this.ready;
    await this.fns.signOut(this.auth);
  }
  async sendPasswordReset(email: string): Promise<AuthResult> {
    await this.ready;
    try {
      await this.fns.resetEmail(this.auth, email.trim());
      return { ok: true };
    } catch (e) {
      // Ne pas révéler si l'email existe : succès affiché sauf email invalide.
      const code = errCode(e);
      if (code === 'auth/invalid-email') return { ok: false, error: mapError(code) };
      return { ok: true };
    }
  }
  async saveData(data: Record<string, string>): Promise<void> {
    await this.ready;
    if (!this.user) return;
    await this.fns.setDoc(this.fns.doc(this.db, 'saves', this.user.uid), {
      data,
      updatedAt: Date.now(),
    });
  }
  async loadData(): Promise<SavePayload | null> {
    await this.ready;
    if (!this.user) return null;
    const snap = await this.fns.getDoc(this.fns.doc(this.db, 'saves', this.user.uid));
    if (!snap.exists()) return null;
    const d = snap.data() as SavePayload;
    return { data: d.data ?? {}, updatedAt: typeof d.updatedAt === 'number' ? d.updatedAt : 0 };
  }
}
